/**
 * Generic log viewer mainly targeted for use in internal tools.
 */
import type { MutableRefObject } from "react";
import { useRef, useState } from "react";
import IconClearHistory from "ui/icons/IconClearHistory";
import IconEye from "ui/icons/IconEye";
import IconEyeClosed from "ui/icons/IconEyeClosed";
import styles from "./DebugLogViewer.module.scss";

enum LogMessageType {
  ERROR,
  INFO,
}

export interface DebugLogMessage {
  type: LogMessageType;
  message: string;
  timestamp: number; // milliseconds since epoch
}

export function useDebugLogViewer() {
  const logMessages = useRef<DebugLogMessage[]>([]);
  const [logMessagesGeneration, setLogMessagesGeneration] = useState<number>(0); // Used to trigger a re-render
  const logElementRef = useRef<HTMLDivElement | null>(null);

  function refreshLog() {
    setTimeout(() => {
      // This needs to happen after React re-renders
      if (logElementRef.current) {
        logElementRef.current.scrollTop = logElementRef.current.scrollHeight;
      }
    }, 0);
    setLogMessagesGeneration(logMessagesGeneration + 1);
  }

  function addErrorMessage(msg: string) {
    logMessages.current.push({ type: LogMessageType.ERROR, message: msg, timestamp: Date.now() });
    refreshLog();
  }

  function addLogMessage(msg: string) {
    logMessages.current.push({ type: LogMessageType.INFO, message: msg, timestamp: Date.now() });
    refreshLog();
  }

  return {
    addErrorMessage,
    addLogMessage,
    logElementRef,
    logMessages,
  };
}

interface DebugLogViewerProps {
  logMessages: MutableRefObject<DebugLogMessage[]>;
  logElementRef: MutableRefObject<HTMLDivElement | null>;
}

export default function DebugLogViewer({ logMessages, logElementRef }: DebugLogViewerProps) {
  const [gen, setGen] = useState<number>(0);
  const [showTimestamp, setShowTimestamp] = useState<boolean>(false);

  return (
    <div className={styles.container}>
      <div className={styles.buttons}>
        <button
          className={styles.button}
          onClick={() => {
            logMessages.current = [];
            setGen(gen + 1); // Trigger a re-render
          }}
          title="Clear log"
          type="button"
        >
          <IconClearHistory />
        </button>
        <button
          className={styles.button}
          onClick={() => {
            setShowTimestamp(!showTimestamp);
          }}
          title="Show timestamps"
          type="button"
        >
          {showTimestamp ? <IconEye /> : <IconEyeClosed />}
        </button>
      </div>
      <div className={styles.wrapper} ref={logElementRef}>
        {logMessages.current.map((msg, i) => (
          <p key={i} className={msg.type === LogMessageType.ERROR ? styles.error : ""}>
            {showTimestamp && (
              <span className={styles.timestamp}>
                {new Date(msg.timestamp).getHours().toString().padStart(2, "0")}:
                {new Date(msg.timestamp).getMinutes().toString().padStart(2, "0")}:
                {new Date(msg.timestamp).getSeconds().toString().padStart(2, "0")}
              </span>
            )}{" "}
            {msg.message}
          </p>
        ))}
      </div>
    </div>
  );
}
